import React, { useCallback, useEffect, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { v4 as uuidv4 } from "uuid"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faXmark,
  faCopy,
  faThumbsUp,
  faThumbsDown,
  faRotateRight,
  faArrowUp,
  faStar as faSparkles
} from "@fortawesome/free-solid-svg-icons"
import styles from "./chatwindow.module.scss"
import { useStartChat } from "./hooks/useChathook"
import { useGetSuggestions } from "./hooks/useGetSuggestions"
import { isFedRampEnvironment } from "src/utils/CommonMethods"

interface Message {
  message: string
  actor: "bot" | "user"
}

interface AiChatProps {
  isVisible?: boolean
  onWindowClose?: () => void
  buildingId?: string
  equipmentId?: string
  systemId?: string
}

const AiChat: React.FC<AiChatProps> = ({
  isVisible = false,
  onWindowClose = () => {
    /* default no-op */
  },
  buildingId = "",
  equipmentId = "",
  systemId = ""
}) => {
  const [message, setMessage] = useState<string>("")
  const [messagesList, setMessagesList] = useState<Message[]>([])
  const [sessionId, setSessionId] = useState<string>("")
  const [currentBuildingId, setCurrentBuildingId] = useState<string>(buildingId)
  const [isWaitingForResponse, setIsWaitingForResponse] =
    useState<boolean>(false)

  // Ref for auto-scrolling to latest message
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const isFedRamp = isFedRampEnvironment()
  const inputRef = useRef<HTMLInputElement>(null)
  const prevIsStreamingRef = useRef<boolean>(false)

  // Resizing state - 90% of window height
  const [windowSize, setWindowSize] = useState({
    width: 442,
    height: window.innerHeight - 110
  })

  //  Resize handler for both width and height
  const handleResize = useCallback(
    (resizeType: "width" | "height") => (e: React.MouseEvent) => {
      e.preventDefault()

      // Get current window size from the DOM element instead of stale state
      const container = e.currentTarget.parentElement as HTMLElement
      const currentWidth = container.offsetWidth
      const currentHeight = container.offsetHeight

      const isWidthResize = resizeType === "width"
      const startPos = isWidthResize ? e.clientX : e.clientY
      const startSize = isWidthResize ? currentWidth : currentHeight

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (isWidthResize) {
          // Calculate delta - positive when expanding (dragging left), negative when shrinking (dragging right)
          const deltaX = startPos - moveEvent.clientX
          const newWidth = startSize + deltaX

          // Apply constraints smoothly
          const constrainedWidth = Math.max(300, newWidth)

          setWindowSize((prev) => ({ ...prev, width: constrainedWidth }))
        } else {
          // Height resize - positive when expanding (dragging up), negative when shrinking (dragging down)
          const deltaY = startPos - moveEvent.clientY
          let newHeight = startSize + deltaY

          newHeight = Math.min(newHeight, window.innerHeight - 40) // Max height based on viewport with some margin

          // Apply constraints smoothly
          const constrainedHeight = Math.max(300, newHeight)
          setWindowSize((prev) => ({ ...prev, height: constrainedHeight }))
        }
      }

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove)
        document.removeEventListener("mouseup", handleMouseUp)
      }

      document.addEventListener("mousemove", handleMouseMove)
      document.addEventListener("mouseup", handleMouseUp)
    },
    [] // Empty dependencies - function doesn't depend on any external values
  )

  // Initialize session ID and start subscription when chat becomes visible
  useEffect(() => {
    if (isVisible && !sessionId) {
      const newSessionId = uuidv4()
      setSessionId(newSessionId)
    }
  }, [isVisible, sessionId])

  // Auto-scroll to bottom when chat becomes visible (on open)
  useEffect(() => {
    if (isVisible && messagesContainerRef.current && messagesList.length > 0) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTo({
            top: messagesContainerRef.current.scrollHeight,
            behavior: "smooth"
          })
        }
      }, 100)
    }
  }, [isVisible, messagesList.length])

  // Handle building ID changes - clear messages only when building changes
  useEffect(() => {
    if (buildingId !== currentBuildingId && currentBuildingId !== "") {
      // Clear messages and reset chat state for new building
      const welcomeMessage: Message[] = [
        {
          message: "Hello! How can I assist you with this building today?",
          actor: "bot"
        }
      ]
      setMessagesList(welcomeMessage)

      // Reset session for new building context
      const newSessionId = uuidv4()
      setSessionId(newSessionId)
    }

    // Update current building ID
    setCurrentBuildingId(buildingId)
  }, [buildingId, currentBuildingId])

  // Initialize with welcome message only on first load
  useEffect(() => {
    if (messagesList.length === 0) {
      const welcomeMessage: Message[] = [
        { message: "Hello! How can I assist you today?", actor: "bot" }
      ]
      setMessagesList(welcomeMessage)
    }
  }, [])

  // Initialize hooks
  const {
    startChat,
    isLoading: isStartingChat,
    error: startChatError,
    streamedMessage,
    isStreaming
  } = useStartChat()

  const {
    suggestions,
    isLoadingSuggestions,
    fetchSuggestions,
    clearSuggestions
  } = useGetSuggestions()

  // Handle HTTP streaming messages from useStartChat hook
  useEffect(() => {
    console.log("streamedMessage received:", streamedMessage)
    if (streamedMessage) {
      // Once we receive streamed messages, stop waiting
      setIsWaitingForResponse(false)

      setMessagesList((prev) => {
        const lastMessage = prev[prev.length - 1]

        // Create new bot message only if last message is from user
        if (!lastMessage || lastMessage.actor === "user") {
          return [...prev, { message: streamedMessage, actor: "bot" as const }]
        }

        // If last message is from bot, update it with the streamed content
        if (lastMessage.actor === "bot") {
          return prev.map((msg, index) =>
            index === prev.length - 1
              ? { ...msg, message: streamedMessage }
              : msg
          )
        }

        return prev
      })
    }
  }, [streamedMessage])

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: "smooth"
      })
    }
  }, [messagesList])

  // Fetch suggestions when chat opens
  useEffect(() => {
    if (isVisible) {
      fetchSuggestions()
    }
  }, [isVisible, fetchSuggestions])

  // Fetch suggestions after each bot response completes
  useEffect(() => {
    if (prevIsStreamingRef.current && !isStreaming) {
      fetchSuggestions()
    }
    prevIsStreamingRef.current = isStreaming
  }, [isStreaming, fetchSuggestions])

  // Focus input field when chat becomes visible and connected
  useEffect(() => {
    if (
      isVisible &&
      inputRef.current &&
      !isStartingChat &&
      !isWaitingForResponse &&
      !isStreaming
    ) {
      const timer = setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isVisible, isStartingChat, isWaitingForResponse, isStreaming])

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation() // Prevent event bubbling

    // Preserve chat history and session for same building
    onWindowClose()
  }

  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation() // Prevent clicks inside the chat from bubbling up
  }

  const handleMessageChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setMessage(e.target.value)
  }

  const sendChatMessage = async (userMessage: string): Promise<void> => {
    if (!sessionId || isStartingChat || isStreaming) {
      return
    }

    const newMessage: Message = { message: userMessage, actor: "user" }

    // Add user message to list
    setMessagesList((prev) => [...prev, newMessage])

    // Set waiting state to true when we start the request
    setIsWaitingForResponse(true)

    try {
      // Send the chat mutation (subscription is already established)
      const result = await startChat({
        message: `${userMessage}`,
        //buildingId,
        sessionId
      })

      if (
        result &&
        (result.status === "started" ||
          result.status === "completed_sync" ||
          result?.status === "completed")
      ) {
        // Success
      } else {
        throw new Error("Failed to start chat")
      }
    } catch (error) {
      console.error("Error sending message:", error)
      setIsWaitingForResponse(false) // Reset waiting state on error
    }
  }

  const handleSendMessage = async (): Promise<void> => {
    if (message.trim()) {
      const userMessage = message.trim()
      setMessage("") // Clear input field
      await sendChatMessage(userMessage)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleCopyResponse = useCallback(() => {
    const lastBotMessage = [...messagesList]
      .reverse()
      .find((msg) => msg.actor === "bot")
    if (lastBotMessage) {
      navigator.clipboard
        .writeText(lastBotMessage.message)
        .then(() => {
          console.log("Response copied to clipboard")
          // You can add a toast notification here later
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err)
        })
    }
  }, [messagesList])

  const handleRetry = useCallback(async () => {
    const lastUserMessage = [...messagesList]
      .reverse()
      .find((msg) => msg.actor === "user")

    if (lastUserMessage && !isWaitingForResponse) {
      await sendChatMessage(lastUserMessage.message)
    }
  }, [messagesList, isWaitingForResponse, sendChatMessage])

  return (
    isVisible &&
    !isFedRamp && (
      <div
        className={styles.containerWrapper}
        onClick={handleContainerClick}
        style={{
          width: windowSize.width,
          height: windowSize.height
        }}
      >
        {/* Width resize handle (left side) */}
        <div
          className={styles.resizeHandleWidth}
          onMouseDown={handleResize("width")}
        />

        {/* Height resize handle (top side) */}
        <div
          className={styles.resizeHandleHeight}
          onMouseDown={handleResize("height")}
        />

        <div className={styles.mainWindow}>
          <div className={styles.header}>
            <div className={styles.headerTitle}>
              <div className={styles.askAILogoContainer}>
                <div className={styles.askAILogoWrapper}>
                  <FontAwesomeIcon icon={faSparkles} color="white" />
                </div>
              </div>
              <span>Ask AI</span>
            </div>
            <div className={styles.closeButtonContainer}>
              <button onClick={handleClose} className={styles.closeButton}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
          </div>
          <hr className={styles.headerSeparator} />
          <div className={styles.body} ref={messagesContainerRef}>
            {messagesList.map((msg, index) => (
              <div
                key={index}
                className={`${styles.messageContainer} ${styles[msg.actor]}`}
              >
                <div
                  className={`${styles.message} ${
                    styles[msg.actor + "Message"]
                  }`}
                >
                  {msg.actor === "user" ? (
                    msg.message
                  ) : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {msg.message}
                    </ReactMarkdown>
                  )}
                </div>

                {/* Feedback buttons for bot messages - exclude first welcome message */}
                {msg.actor === "bot" &&
                  index > 0 &&
                  index === messagesList.length - 1 &&
                  !isWaitingForResponse &&
                  !isStartingChat && (
                    <>
                      <p className={styles.aiGeneratedLabel}>
                        AI generated response
                      </p>
                      <div className={styles.feedbackButtons}>
                        <button
                          className={styles.feedbackButton}
                          onClick={handleCopyResponse}
                        >
                          <FontAwesomeIcon icon={faCopy} />
                        </button>
                        <button className={styles.feedbackButton}>
                          <FontAwesomeIcon icon={faThumbsUp} />
                        </button>
                        <button className={styles.feedbackButton}>
                          <FontAwesomeIcon icon={faThumbsDown} />
                        </button>
                        <button
                          className={styles.feedbackButton}
                          onClick={handleRetry}
                        >
                          <FontAwesomeIcon icon={faRotateRight} />
                        </button>
                      </div>
                    </>
                  )}
              </div>
            ))}

            {/* AI is thinking indicator */}
            {(isWaitingForResponse || isStartingChat) && (
              <div className={`${styles.messageContainer} ${styles.bot}`}>
                <div className={`${styles.message} ${styles.botMessage}`}>
                  <div className={styles.loaderMessage}>AI is thinking...</div>
                </div>
              </div>
            )}
          </div>
          <div className={styles.footer}>
            <div
              className={`${styles.inputContainer} ${
                isWaitingForResponse || isStartingChat ? styles.processing : ""
              }`}
            >
              <input
                ref={inputRef}
                type="text"
                placeholder={
                  isWaitingForResponse || isStartingChat || isStreaming
                    ? "AI is thinking..."
                    : "Ask anything"
                }
                value={message}
                onChange={handleMessageChange}
                onKeyPress={handleKeyPress}
                disabled={isWaitingForResponse || isStartingChat || isStreaming}
              />
              <div
                onClick={handleSendMessage}
                className={styles.messageButtonContainer}
              >
                <FontAwesomeIcon icon={faArrowUp} color="white" />
              </div>
            </div>
            {suggestions.length > 0 &&
              !isWaitingForResponse &&
              !isStartingChat &&
              !isStreaming && (
                <div className={styles.suggestionsContainer}>
                  {suggestions.map((suggestion, i) => (
                    <button
                      key={i}
                      className={styles.suggestionChip}
                      onClick={() => {
                        clearSuggestions()
                        sendChatMessage(suggestion)
                      }}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            {startChatError && (
              <div className={styles.errorMessage}>
                {startChatError.message}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  )
}

export default AiChat

